import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

const PlayerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlayPauseButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background-color: #f8f1ff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 2px;
  margin-right: 14px;

  &:hover {
    background-color: #9738ff;

    svg {
      fill: #fff;
      path {
        fill: #fff;
      }
    }
  }

  svg {
    width: 30px;
    height: 30px;
    fill: #9738ff;
  }

  .play_icon {
    width: 14px;
    height: 18px;
    margin-left: 4px;
  }

  .pause_icon {
    width: 12px;
    height: 16px;
    margin-left: 0px;
  }
`;

const PlayIcon = () => (
  <svg
    className="play_icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 14 18"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.9375 3.17511V14.8237L10.9975 8.99938L1.9375 3.17511ZM1.56437 1.00342L12.9394 8.31592C13.4369 8.63575 13.4369 9.36301 12.9394 9.68284L1.56437 16.9953C1.02364 17.343 0.3125 16.9547 0.3125 16.3119V1.68688C0.3125 1.04405 1.02364 0.655807 1.56437 1.00342Z"
    />
  </svg>
);

const PauseIcon = () => (
  <svg
    className="pause_icon"
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="16"
    viewBox="0 0 12 16"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.9375 2.3125V13.6875H3.5625V2.3125H1.9375ZM1.125 0.6875H4.375C4.82373 0.6875 5.1875 1.05127 5.1875 1.5V14.5C5.1875 14.9487 4.82373 15.3125 4.375 15.3125H1.125C0.676269 15.3125 0.3125 14.9487 0.3125 14.5V1.5C0.3125 1.05127 0.676269 0.6875 1.125 0.6875Z"
      fill="#9738FF"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.4375 2.3125V13.6875H10.0625V2.3125H8.4375ZM7.625 0.6875H10.875C11.3237 0.6875 11.6875 1.05127 11.6875 1.5V14.5C11.6875 14.9487 11.3237 15.3125 10.875 15.3125H7.625C7.17627 15.3125 6.8125 14.9487 6.8125 14.5V1.5C6.8125 1.05127 7.17627 0.6875 7.625 0.6875Z"
      fill="#9738FF"
    />
  </svg>
);

const SimpleAudioPlayer = ({
  src,
  voiceId,
  stopAllAudioPlayersExcept,
  registerAudioPlayer,
  unregisterAudioPlayer
}: {
  src: string;
  voiceId?: string;
  stopAllAudioPlayersExcept?: (exceptVoiceId?: string) => void;
  registerAudioPlayer?: (
    voiceId: string,
    audioElement: HTMLAudioElement
  ) => void;
  unregisterAudioPlayer?: (voiceId: string) => void;
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (audioRef.current && registerAudioPlayer && voiceId) {
      registerAudioPlayer(voiceId, audioRef.current);
    }

    return () => {
      if (unregisterAudioPlayer && voiceId) {
        unregisterAudioPlayer(voiceId);
      }
    };
  }, [voiceId, registerAudioPlayer, unregisterAudioPlayer]);

  const togglePlayback = () => {
    if (!audioRef.current || !voiceId) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      if (stopAllAudioPlayersExcept) {
        stopAllAudioPlayersExcept(voiceId);
      }
      audioRef.current.play().catch(() => {
        // Silently ignore autoplay restrictions
      });
      setIsPlaying(true);
    }
  };

  return (
    <PlayerWrapper>
      <audio
        ref={audioRef}
        src={src}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
      />
      <PlayPauseButton onClick={togglePlayback}>
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </PlayPauseButton>
    </PlayerWrapper>
  );
};

export default SimpleAudioPlayer;
